import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bare OBS Browser-Source overlay. Transparent background. Renders donation
 * alerts (show logo, donor Discord avatar/name, message, amount, AI video) one
 * at a time as they arrive over the WebSocket (with an HTTP poll fallback).
 * Rendered OUTSIDE <Layout> so there's no navbar/footer chrome.
 */
export default function Overlay() {
  const { token } = useParams();
  const [current, setCurrent] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const queueRef = useRef([]);
  const seenRef = useRef(new Set());
  const busyRef = useRef(false);
  const videoRef = useRef(null);

  // Transparent page for OBS.
  useEffect(() => {
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, []);

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => {
      setCurrent(null);
      setLeaving(false);
      busyRef.current = false;
    }, 600);
  }, []);

  const pump = useCallback(() => {
    if (busyRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    busyRef.current = true;
    setLeaving(false);
    setCurrent(next);
  }, []);

  const enqueue = useCallback((alert) => {
    if (!alert || alert.type !== "alert" || !alert.id) return;
    if (seenRef.current.has(alert.id)) return;
    seenRef.current.add(alert.id);
    queueRef.current.push(alert);
    pump();
  }, [pump]);

  // Auto-advance for text-only alerts (video alerts advance on `ended`).
  useEffect(() => {
    if (!current || current.video_url) return;
    const secs = Math.max(3, Number(current.alert_duration_secs || 7));
    const t = setTimeout(dismiss, secs * 1000);
    return () => clearTimeout(t);
  }, [current, dismiss]);

  // When a video alert is showing, try to play with audio; fall back to muted.
  useEffect(() => {
    if (!current || !current.video_url || !videoRef.current) return;
    const v = videoRef.current;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {});
    });
    const failSafe = setTimeout(dismiss, 30000); // never hang forever
    return () => clearTimeout(failSafe);
  }, [current, dismiss]);

  // WebSocket (realtime) + poll fallback.
  useEffect(() => {
    let ws;
    let closed = false;
    let reconnectTimer;
    const wsBase = BACKEND_URL.replace(/^http/, "ws");

    const connect = () => {
      try {
        ws = new WebSocket(`${wsBase}/api/ws/overlay/${token}`);
        ws.onmessage = (ev) => {
          try { enqueue(JSON.parse(ev.data)); } catch { /* ignore */ }
        };
        ws.onopen = () => {
          // keepalive ping so proxies don't drop idle sockets
          ws._ka = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000);
        };
        ws.onclose = () => {
          if (ws._ka) clearInterval(ws._ka);
          if (!closed) reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      } catch {
        if (!closed) reconnectTimer = setTimeout(connect, 3000);
      }
    };
    connect();

    const doPoll = async () => {
      try {
        const res = await fetch(`${API_BASE}/overlay/${token}/poll`);
        if (!res.ok) return;
        const data = await res.json();
        (data.alerts || []).forEach((a) => enqueue(a));
      } catch { /* ignore */ }
    };
    doPoll(); // immediate — don't wait a full interval for the first alert
    const poll = setInterval(doPoll, 4000);

    return () => {
      closed = true;
      clearInterval(poll);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { if (ws._ka) clearInterval(ws._ka); try { ws.close(); } catch { /* noop */ } }
    };
  }, [token, enqueue]);

  return (
    <div
      data-testid="overlay-root"
      style={{ position: "fixed", inset: 0, background: "transparent", overflow: "hidden", fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
    >
      <style>{`
        @keyframes ovIn { 0% { transform: translateY(40px) scale(.96); opacity: 0; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
        @keyframes ovOut { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(30px) scale(.97); opacity: 0; } }
        @keyframes ovBar { 0% { width: 100%; } 100% { width: 0%; } }
      `}</style>

      {current && (
        <div
          data-testid="overlay-alert"
          data-alert-id={current.id}
          style={{
            position: "absolute",
            left: "50%",
            bottom: "6vh",
            transform: "translateX(-50%)",
            width: "min(760px, 92vw)",
            animation: `${leaving ? "ovOut" : "ovIn"} .6s cubic-bezier(.2,.8,.2,1) forwards`,
          }}
        >
          <div style={{
            border: "1px solid #FF3B30",
            borderRadius: 14,
            overflow: "hidden",
            background: "rgba(10,10,10,0.82)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            boxShadow: "0 24px 80px rgba(0,0,0,.55), 0 0 0 1px rgba(255,59,48,.25)",
          }}>
            {/* top ribbon */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", background: "linear-gradient(135deg, #FF3B30 0%, #C92A21 100%)" }}>
              {current.show_logo_url ? (
                <img src={current.show_logo_url} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: "cover", boxShadow: "0 0 0 2px rgba(255,255,255,.35)" }} />
              ) : null}
              <div style={{ fontWeight: 700, letterSpacing: ".18em", fontSize: 12, textTransform: "uppercase", color: "#fff" }}>
                New {current.kind === "donation" ? "Donation" : "Alert"}
              </div>
              <div style={{ marginLeft: "auto", fontWeight: 800, fontSize: 22, color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,.35)" }} data-testid="overlay-amount">
                {current.amount_display}
              </div>
            </div>

            {/* video */}
            {current.video_url && (
              <video
                ref={videoRef}
                src={current.video_url}
                data-testid="overlay-video"
                autoPlay
                playsInline
                onEnded={dismiss}
                style={{ width: "100%", display: "block", background: "#000", aspectRatio: "16 / 9", objectFit: "cover" }}
              />
            )}

            {/* body */}
            <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "16px 18px" }}>
              {(current.donor_avatar || current.show_logo_url) && (
                <img
                  src={current.donor_avatar || current.show_logo_url}
                  alt=""
                  style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", boxShadow: "0 0 0 2px #FF3B30" }}
                />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span data-testid="overlay-donor" style={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>
                    {current.donor_name || "Anonymous"}
                  </span>
                  {current.donor_discord_name && (
                    <span style={{ color: "#A3A3A3", fontSize: 12, fontFamily: "monospace" }}>@{current.donor_discord_name}</span>
                  )}
                  {current.character && (
                    <span style={{ marginLeft: "auto", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "#FF3B30", border: "1px solid rgba(255,59,48,.5)", borderRadius: 999, padding: "2px 8px" }}>
                      {current.character}
                    </span>
                  )}
                </div>
                {current.message && (
                  <div data-testid="overlay-message" style={{ color: "#e9e9e9", fontSize: 15, marginTop: 6, lineHeight: 1.45, wordBreak: "break-word" }}>
                    {current.message}
                  </div>
                )}
              </div>
            </div>

            {/* progress bar for text-only alerts */}
            {!current.video_url && !leaving && (
              <div style={{ height: 3, background: "rgba(255,255,255,.08)" }}>
                <div style={{ height: "100%", background: "#FF3B30", animation: `ovBar ${Math.max(3, Number(current.alert_duration_secs || 7))}s linear forwards` }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
